import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import ApprovalItemsStatus from '@/components/Approval/@components/ApprovalItemsStatus';
import {
  countPlmCards,
  type PlmRejectTarget,
  ProductLifecycleSnapshot,
} from '@/components/Approval/@components/ProductLifecycle';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { parseProductLifecycleAttributes } from '@/utils/approval/productLifecycle';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const REASON_MAX_LENGTH = 300;

/**
 * 123 GERP PLM — 저장 카드 HTML 그대로(레거시 문서 포함). GERP 재조회 안 함.
 * 부분반려 버튼 노출: 문서 진행중 + 지금 내 결재 차례 + 카드 2건 이상(레거시 조건)
 */
const ProductLifecycleSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const { data: view } = useApprovalView(data.documentId);
  const myInfo = useAtomValue(MyInfoState);
  const [target, setTarget] = useState<PlmRejectTarget | null>(null);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  const state = parseProductLifecycleAttributes(data.attributes);
  const canPartialReject =
    !!view &&
    !!state &&
    view.status === 'P' &&
    isCurrentApproverMeOf(
      view.approvers,
      view.currentApproverOrder,
      myInfo?.passportId ?? '',
      view.status,
    ) &&
    countPlmCards(state.content) >= 2;

  const handleSubmit = async (reason: string) => {
    if (!target) return;
    try {
      await partialReject(target.itemId, reason);
      setTarget(null);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  if (!state) return null;
  return (
    <ContentSlotRoot>
      <ApprovalItemsStatus
        documentId={data.documentId}
        dangerStatuses={['EXCLUDED', 'REJECTED']}
        messages={{
          loading: '품번 조회중...',
          empty: '표시할 품번이 없습니다.',
          error: '품번 조회 실패',
        }}
      />
      <ProductLifecycleSnapshot
        content={state.content}
        plmType={state.plmType}
        onPartialReject={canPartialReject ? setTarget : undefined}
      />
      <ReasonInputModal
        open={!!target}
        onClose={() => setTarget(null)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{styleNo}} 반려', { styleNo: target?.styleNo ?? '' })}
        description={t('이 품번만 결재에서 제외됩니다.')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder={t('사유를 입력해주세요.')}
        reasonMaxLength={REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default ProductLifecycleSlot;
